import { createRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Route as appRoute } from '../../../_app'
import {
  QuizEditor,
  type EditorQuestion,
  type EditorQuiz,
} from '../../../../components/QuizEditor'

/**
 * Edit-quiz route. Hydrates the editor from `GET /api/v1/quizzes/{id}` and
 * fires `PUT /api/v1/quizzes/{id}` on save — the API uses replace-all
 * semantics so we just send the canonical full payload every time. The
 * list cache (`['quizzes']`) is invalidated on success so the index page
 * shows fresh `questionCount` / `updatedAt` values when the host
 * navigates back.
 *
 * The shape mismatch between the API's `QuizDetailDto` and the editor's
 * internal `EditorQuiz` is bridged by `toEditorQuiz` (read) and
 * `toQuestionPayload` (write); keeping the editor agnostic of the wire
 * format makes the component reusable across the create/edit pages.
 */
export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/quizzes/$quizId/edit',
  component: EditQuizPage,
})

type ApiQuestion = {
  id: string
  text: string
  timeSeconds: number
  order: number
  type: 'MultipleChoice' | 'TrueFalse' | 'OrderSequence'
  options: string[] | null
  correctOptionIndex: number | null
  correctAnswer: boolean | null
  items: string[] | null
  explanation: string | null
}

type ApiQuizDetail = {
  id: string
  title: string
  description: string
  questions: ApiQuestion[]
  updatedAt: string
}

type QuestionPayload = {
  text: string
  timeSeconds: number
  type: 'MultipleChoice' | 'TrueFalse' | 'OrderSequence'
  options?: string[]
  correctOptionIndex?: number
  correctAnswer?: boolean
  items?: string[]
  explanation?: string | undefined
}

function EditQuizPage() {
  const { t } = useTranslation('quiz')
  const { t: tCommon } = useTranslation('common')
  const { quizId } = Route.useParams()
  const queryClient = useQueryClient()

  const quizQuery = useQuery({
    queryKey: ['quizzes', quizId],
    queryFn: async ({ signal }) => {
      const res = await fetch(`/api/v1/quizzes/${encodeURIComponent(quizId)}`, {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load quiz: ${res.status}`)
      }
      return (await res.json()) as ApiQuizDetail
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (quiz: EditorQuiz) => {
      const res = await fetch(`/api/v1/quizzes/${encodeURIComponent(quizId)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: quiz.title,
          description: quiz.description,
          questions: quiz.questions.map(toQuestionPayload),
        }),
      })
      if (!res.ok) {
        throw new Error(`Failed to save quiz: ${res.status}`)
      }
    },
    onSuccess: () => {
      // Both the detail cache (this page) and the list cache (index page)
      // must drop their stale snapshots. The detail key match scopes the
      // invalidation to this quiz; the list invalidation refreshes the
      // index's count / updatedAt projections.
      void queryClient.invalidateQueries({ queryKey: ['quizzes'] })
    },
  })

  if (quizQuery.isLoading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-ink-50">
        <div
          className="h-12 w-12 animate-spin rounded-full border-4 border-brand-100 border-t-brand-500"
          role="status"
          aria-live="polite"
        >
          <span className="sr-only">{tCommon('loading')}</span>
        </div>
      </main>
    )
  }

  if (quizQuery.isError || !quizQuery.data) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-ink-50 px-4">
        <div
          className="max-w-md rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 text-center"
          role="alert"
        >
          {t('loadError')}
        </div>
      </main>
    )
  }

  return (
    <QuizEditor
      heading={t('editQuiz')}
      initialQuiz={toEditorQuiz(quizQuery.data)}
      onSave={async (quiz) => {
        await saveMutation.mutateAsync(quiz)
      }}
      isSaving={saveMutation.isPending}
      isError={saveMutation.isError}
    />
  )
}

/**
 * Map the API's polymorphic `QuestionDto` (nullable fields per type) into
 * the editor's discriminated union (each variant only carries the fields
 * relevant to it). Server already sorts by `order` — we preserve that
 * order on the way in.
 */
function toEditorQuiz(detail: ApiQuizDetail): EditorQuiz {
  return {
    title: detail.title,
    description: detail.description,
    questions: detail.questions.map<EditorQuestion>((q) => {
      if (q.type === 'TrueFalse') {
        return {
          type: 'TrueFalse',
          text: q.text,
          timeSeconds: q.timeSeconds,
          correctAnswer: q.correctAnswer ?? true,
          explanation: q.explanation ?? undefined,
        }
      }
      if (q.type === 'OrderSequence') {
        return {
          type: 'OrderSequence',
          text: q.text,
          timeSeconds: q.timeSeconds,
          items: q.items ?? ['', '', '', ''],
          explanation: q.explanation ?? undefined,
        }
      }
      return {
        type: 'MultipleChoice',
        text: q.text,
        timeSeconds: q.timeSeconds,
        options: q.options ?? ['', '', '', ''],
        correctOptionIndex: q.correctOptionIndex ?? 0,
        explanation: q.explanation ?? undefined,
      }
    }),
  }
}

/**
 * Coerce one editor question into the `PUT` payload shape. Mirrors the
 * helper on the create route — kept inline here so each page owns its
 * own wire mapping and tests can mock per-route without cross-coupling.
 */
function toQuestionPayload(
  q: EditorQuiz['questions'][number],
): QuestionPayload {
  if (q.type === 'MultipleChoice') {
    return {
      text: q.text,
      timeSeconds: q.timeSeconds,
      type: 'MultipleChoice',
      options: q.options,
      correctOptionIndex: q.correctOptionIndex,
      explanation: q.explanation?.trim() ? q.explanation.trim() : undefined,
    }
  }
  if (q.type === 'OrderSequence') {
    return {
      text: q.text,
      timeSeconds: q.timeSeconds,
      type: 'OrderSequence',
      items: q.items,
      explanation: q.explanation?.trim() ? q.explanation.trim() : undefined,
    }
  }
  return {
    text: q.text,
    timeSeconds: q.timeSeconds,
    type: 'TrueFalse',
    correctAnswer: q.correctAnswer,
    explanation: q.explanation?.trim() ? q.explanation.trim() : undefined,
  }
}
